import { createContext, ReactNode, useContext, useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useDataProvider, useGetList, useGetOne, useRecordContext } from "react-admin";
import {
  Claimant,
  Client,
  ObjectType,
  Policy,
  PolicyData,
  Reimbursement,
  ReimbursementStatus,
  ResourceName,
  StoplossClaim,
  StoplossClaimStatus,
} from "@/types";
import { isAggregateSpecificPolicy } from "@/resources/clients/components/claims-summary/utils/aggregateSpecific";
import { auraTrackState } from "@/resources/stoploss-claims/twoTrackState";
import { needsFamilyUnit } from "../components/PendingFamilyUnitBanner";
import { useFamilyUnit } from "./useFamilyUnit";
import {
  DeductibleSource,
  getEffectiveDeductible,
  getResolvedClaimsValues,
  isLaseredDeductible,
  useSelectedPolicyData,
} from "./useCurrentPolicyData";

/** One page, one fetch of the claimant's stop-loss claims: the rail and the Claims tab read the same rows. */
const CLAIMS_PAGE_SIZE = 500;
const REIMBURSEMENTS_PAGE_SIZE = 500;

export interface ClaimantPageValue {
  claimant: Claimant;
  claimantId: string;
  client?: Client;
  /** The selected policy and its snapshot (undefined for a claimant with no policy). */
  policy?: Policy;
  policyId?: string;
  policyData?: PolicyData;
  effectiveDeductible: { amount: number; label: string; source: DeductibleSource; reviewPending: boolean };
  isLasered: boolean;
  reviewPending: boolean;
  isASD: boolean;
  /** The policy carries a family deductible endorsement. */
  isFamilyPolicy: boolean;
  familyUnit: ReturnType<typeof useFamilyUnit>;
  /** On a family-deductible policy, linked to a subscriber, and the unit's figures are for this term: the pool sizes the claims. */
  familyLayout: boolean;
  /** On a family-deductible policy with no family unit yet: claims are held. */
  familyUnitMissing: boolean;
  /** Any hold on this claimant's stop-loss processing. */
  held: boolean;
  /** Stop-loss claims on the selected policy (every type, vendor and status), oldest first. */
  claims: StoplossClaim[];
  claimsTotal?: number;
  claimsLoading: boolean;
  claimsError: boolean;
  /**
   * Σ claim amounts of the claims above, every status: the netting set
   * (`SumStoplossClaimsByClaimantAndPolicy`) counts denied claims too, so the
   * count and the sum cover the same rows and reconcile to the spend over the
   * deductible. The denied part is reported separately for the label.
   */
  claimsAmount: number;
  deniedAmount: number;
  /**
   * Σ claim amounts where Aura funding has been requested or funded (the amount
   * Aura receives is `total_paid_amount`). A claim the carrier later denied is
   * still money advanced, so denied claims stay in.
   */
  fundingRequested: number;
  /**
   * Paid reimbursements against this claimant's claims on the policy, read
   * through each claim (`stoploss_claim_id_ref`, which also walks the M2M
   * allocation junction) and deduplicated by reimbursement id. Not read by the
   * list's `policy_id_ref` filter: that column is nullable and a run-out payment
   * can land on the other term or on none (claimant-authority review 2026-10-27).
   */
  reimbursements: Reimbursement[];
  reimbursedTotal: number;
  reimbursementsLoading: boolean;
  openTaskCount?: number;
  accelEnabled: boolean;
}

const ClaimantPageContext = createContext<ClaimantPageValue | null>(null);

/**
 * Page-level data for the claimant show page (Feature #7659). Lives inside the
 * PolicyProvider so the figures follow the selected policy term. The money lines
 * (funding requested, reimbursed so far) are the claimant-level
 * read of the figures the claim page shows per claim; their definitions were
 * reviewed with the claimant-authority expert (see design-brief.md).
 */
export const ClaimantPageProvider = ({ children }: { children: ReactNode }) => {
  const claimant = useRecordContext<Claimant>();
  const claimantId = claimant?.id ? String(claimant.id) : "";
  const { policy, policyId, policyData } = useSelectedPolicyData();

  const { data: client } = useGetOne<Client>(
    ResourceName.CLIENTS,
    { id: claimant?.client_id_ref ?? "" },
    { enabled: !!claimant?.client_id_ref }
  );

  const familyUnit = useFamilyUnit(claimant?.subscriber_ref_id);

  const claimsFilter = useMemo(() => {
    const f: Record<string, string> = { claimant_id_ref: claimantId };
    if (policyId) f.policy_id_ref = policyId;
    return f;
  }, [claimantId, policyId]);

  const {
    data: claims = [],
    total: claimsTotal,
    isLoading: claimsLoading,
    error: claimsError,
  } = useGetList<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    {
      filter: claimsFilter,
      pagination: { page: 1, perPage: CLAIMS_PAGE_SIZE },
      sort: { field: "created_datetime", order: "ASC" },
    },
    { enabled: !!claimantId }
  );

  const dataProvider = useDataProvider();
  const reimbursementQueries = useQueries({
    queries: claims.map((c) => ({
      queryKey: ["claimant-page-reimbursements", String(c.id)],
      queryFn: async () => {
        const { data } = await dataProvider.getList<Reimbursement>(ResourceName.REIMBURSEMENTS, {
          // No sort: the sum needs none, and `payment_date` is not a sortable field on
          // GET /reimbursements (400 COM-000002; review !5050 [AEM-reimb-sort-400]).
          filter: { stoploss_claim_id_ref: String(c.id) },
          pagination: { page: 1, perPage: REIMBURSEMENTS_PAGE_SIZE },
        });
        return data;
      },
    })),
  });
  const reimbursementsLoading = reimbursementQueries.some((q) => q.isLoading);
  // The query objects are new on every render; key the memo on when each one last got data.
  const reimbursementsStamp = reimbursementQueries.map((q) => q.dataUpdatedAt).join(",");
  const reimbursementRows = useMemo(() => {
    const seen = new Map<string, Reimbursement>();
    for (const q of reimbursementQueries) for (const r of q.data ?? []) seen.set(String(r.id), r);
    return [...seen.values()];
  }, [reimbursementsStamp]);

  const { total: openTaskCount } = useGetList(
    ResourceName.TASKS,
    {
      filter: {
        "record.object_type": ObjectType.CLAIMANTS,
        "record.object_id": claimantId,
        completed: false,
        deleted: false,
      },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: !!claimantId }
  );

  const value = useMemo<ClaimantPageValue | null>(() => {
    if (!claimant) return null;
    const effective = getEffectiveDeductible(policyData, policy);
    // A subscriber-linked claimant on a family-deductible policy is sized on the
    // family pool whatever the member count (orchestrator family path). The
    // family figures (family_total_spend, #7406) belong to the subscriber's term,
    // so the family shape shows only when that is the selected term; a unit
    // without a term never gets it (review !5050, expert medium 4).
    const familyLayout =
      !!claimant.subscriber_ref_id &&
      Boolean(policy?.has_family_deductible_endorsement) &&
      !!familyUnit.data &&
      !!familyUnit.data.subscriber?.policy_id_ref &&
      familyUnit.data.subscriber.policy_id_ref === policyId;
    const familyUnitMissing = needsFamilyUnit(claimant);
    const reviewPending = !!policyData?.is_deductible_review_pending;
    const claimsAmount = claims.reduce((s, c) => s + (c.total_paid_amount ?? 0), 0);
    const deniedAmount = claims
      .filter((c) => c.claim_status === StoplossClaimStatus.DENIED)
      .reduce((s, c) => s + (c.total_paid_amount ?? 0), 0);
    const fundingRequested = claims
      .filter((c) => {
        const s = auraTrackState(c);
        return s === "requested" || s === "funded";
      })
      .reduce((s, c) => s + (c.total_paid_amount ?? 0), 0);
    const reimbursements = reimbursementRows.filter((r) => r.status === ReimbursementStatus.PAID);
    const reimbursedTotal = reimbursements.reduce((s, r) => s + (r.reimbursement_amount ?? 0), 0);
    return {
      claimant,
      claimantId,
      client,
      policy,
      policyId,
      policyData,
      effectiveDeductible: effective,
      isLasered: isLaseredDeductible(effective, policy),
      reviewPending,
      isASD: isAggregateSpecificPolicy(policy),
      isFamilyPolicy: Boolean(policy?.has_family_deductible_endorsement),
      familyUnit,
      familyLayout,
      familyUnitMissing,
      held: reviewPending || familyUnitMissing,
      claims,
      claimsTotal,
      claimsLoading,
      claimsError: !!claimsError,
      claimsAmount,
      deniedAmount,
      fundingRequested,
      reimbursements,
      reimbursedTotal,
      reimbursementsLoading,
      openTaskCount,
      accelEnabled: Boolean(policy?.is_accel_funding_enabled),
    };
  }, [
    claimant,
    claimantId,
    client,
    policy,
    policyId,
    policyData,
    familyUnit,
    claims,
    claimsTotal,
    claimsLoading,
    claimsError,
    reimbursementRows,
    reimbursementsLoading,
    openTaskCount,
  ]);

  if (!value) return null;
  return <ClaimantPageContext.Provider value={value}>{children}</ClaimantPageContext.Provider>;
};

export const useClaimantPage = (): ClaimantPageValue => {
  const ctx = useContext(ClaimantPageContext);
  if (!ctx) throw new Error("useClaimantPage must be used inside ClaimantPageProvider");
  return ctx;
};

/** Spend against the selected policy, authority-resolved, for the rail and the bar. */
export const useClaimantSpend = () => {
  const { policyData } = useClaimantPage();
  return getResolvedClaimsValues(policyData);
};
